import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Comisión ledger API client. The ledger (orgs/{org}/comisiones) is read-only to
// brokers via Firestore rules; every WRITE goes through backend-nyx so the money
// math and the fianza mutation stay atomic and server-authoritative.
//
// Errors carry `code` and `status`. Callers may quietly ignore NO_TARIFA and
// EMISION_EXISTS — the first means the org simply has no tarifa configured yet
// (nothing to book), the second that the movement already exists. Everything
// else, including 403, MUST reach the user: for a money endoso this endpoint is
// the only writer of the contract mutation, so a swallowed failure loses the
// endoso itself, not just the accounting entry.
export const IGNORABLE_LEDGER_CODES = ['NO_TARIFA', 'EMISION_EXISTS'];

/** Whether a ledger error is the expected "nothing to book" kind. */
export function isIgnorableLedgerError(e) {
  return IGNORABLE_LEDGER_CODES.includes(e?.code);
}

async function getAuthToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Usuario no autenticado');
  return await user.getIdToken();
}

const API_BASE = process.env.REACT_APP_API_URL;

function errorFor(status, data) {
  const fallback = 'No se pudo registrar el movimiento de comisión';
  switch (status) {
    case 400: return data.error || 'Parámetros inválidos';
    case 401: return data.error || 'Sesión expirada. Inicia sesión de nuevo.';
    case 403: return data.error || 'No tienes permisos para esta acción';
    case 404: return data.error || 'Recurso no encontrado';
    case 409: return data.error || 'Conflicto al registrar la comisión';
    default: return data.error || fallback;
  }
}

/**
 * Record a money-moving fianza event. The backend writes the contract fianza
 * mutation (when `nextFianza` is supplied) AND the ledger movement(s) in one
 * transaction.
 *
 * @param {Object} payload
 * @param {string} payload.contractId
 * @param {string} payload.fianzaId
 * @param {'emision'|'endoso'|'anulacion'|'cancelacion'|'sustitucion'} payload.eventoTipo
 * @param {string[]} [payload.eventoTipos]   - every change the endoso represents
 * @param {'ninguno'|'adicional'|'sustituye'} [payload.efectoPrima] - THE money axis:
 *        what the endoso did to the prima, independent of what it changed. Only
 *        this books a ledger movement.
 * @param {string} [payload.eventoId]      - the fianza event this movement originates from
 * @param {Object} [payload.nextFianza]    - client-applied fianza element (drives the contract write)
 * @param {Object} [payload.nuevaFianza]   - sustitución: new fianza entity to append
 * @param {Object} [payload.factura]       - new factura entry (adicional / sustituye)
 * @param {number} [payload.cargoOverride] - owner override of the default cargo amount
 * @param {number} [payload.fecha]         - event fecha (ms)
 * @returns {Promise<{status:string, movimientos:string[]}>}
 */
export async function recordComisionEvento(payload) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/evento`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    throw err;
  }
  return data;
}

/**
 * Toggle a ledger movement's estadoCobro. pendiente → cobrada is gated
 * server-side on the source factura being pagada.
 *
 * @param {string} movId
 * @param {'pendiente'|'cobrada'} estadoCobro
 * @returns {Promise<{status:string, movId:string, estadoCobro:string}>}
 */
/**
 * Owner override of an emisión movement's comisión %. The backend recomputes
 * comisionMonto off the stored prima, keeps the previous values under
 * `override`, and re-locks the fianza's comisionPct for future endosos.
 */
export async function overrideComisionPct(movId, comisionPct) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/override-pct`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ movId, comisionPct }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    throw err;
  }
  return data;
}

/**
 * Flip a fianza's factura payment state through the backend — the one write
 * path for `pagada`. Atomically updates the contract's facturas[], the root
 * pagada projection, AND the facturaPagada flag on the ledger movements, so
 * ledger consumers never need the contract to know what is collectable.
 * Omit facturaIds to flip every factura of the fianza.
 */
export async function setFacturaPagada({ contractId, fianzaId, facturaIds = null, pagada }) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/factura-pagada`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ contractId, fianzaId, facturaIds, pagada }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    throw err;
  }
  return data;
}

/**
 * Flip a fianza's beneficiarioConvenio flag; the backend re-prices the pending
 * ledger movements (net fiado share when marked, gross when unmarked) in the
 * same transaction.
 *
 * @param {Object} payload
 * @param {string} payload.contractId
 * @param {string} payload.fianzaId
 * @param {boolean} payload.beneficiarioConvenio
 * @returns {Promise<{status:string, beneficiarioConvenio:boolean, updatedMovimientos:number}>}
 */
export async function setBeneficiarioConvenio(payload) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/beneficiario-convenio`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    throw err;
  }
  return data;
}

/**
 * Batch re-price pending movements after a tarifario change (owner-confirmed).
 *
 * @param {Object} payload
 * @param {Array<{fianzaId:string, comisionPct:number}>} payload.cambios
 * @param {string} [payload.tarifaId] - the table that triggered the retarifa
 * @returns {Promise<{status:string, updatedFianzas:number,
 *                    updatedMovimientos:number, skipped:string[]}>}
 */
export async function retarifarComisiones(payload) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/retarifar`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

// The endpoint caps `cambios` at 200 fianzas and does one Firestore transaction
// per fianza, so a single request is both rejected and slow past that. An org
// that configures its first tarifario with years of movements already booked
// easily blows through it, hence the chunking below.
export const RETARIFA_BATCH_SIZE = 100;

/**
 * Same as {@link retarifarComisiones} but for arbitrarily long plans: sends the
 * changes in sequential batches and aggregates the totals.
 *
 * Batches are independent — each committed one stays committed. If a batch
 * fails, the error carries `parcial` with what landed before it, so the caller
 * can tell the owner how far the retarifa got instead of implying nothing ran.
 * Re-running the plan is safe: the backend skips movements already cobradas or
 * manually overridden, and re-applying the same % is a no-op on the amount.
 *
 * @param {Object} payload
 * @param {Array<{fianzaId:string, comisionPct:number}>} payload.cambios
 * @param {string} [payload.tarifaId]
 * @param {(hechas:number, total:number) => void} [onProgress]
 * @returns {Promise<{status:string, updatedFianzas:number,
 *                    updatedMovimientos:number, skipped:string[]}>}
 */
export async function retarifarComisionesEnLotes({ cambios, tarifaId }, onProgress) {
  const total = (cambios || []).length;
  const acc = { status: 'success', updatedFianzas: 0, updatedMovimientos: 0, skipped: [] };

  for (let i = 0; i < total; i += RETARIFA_BATCH_SIZE) {
    const lote = cambios.slice(i, i + RETARIFA_BATCH_SIZE);
    try {
      const r = await retarifarComisiones({ cambios: lote, tarifaId });
      acc.updatedFianzas += r.updatedFianzas || 0;
      acc.updatedMovimientos += r.updatedMovimientos || 0;
      if (Array.isArray(r.skipped)) acc.skipped.push(...r.skipped);
    } catch (e) {
      // A mid-batch COMMIT_FAILED still applied whatever it got through.
      acc.updatedFianzas += e.data?.updatedFianzas || 0;
      e.parcial = { ...acc, skipped: [...acc.skipped] };
      throw e;
    }
    onProgress?.(Math.min(i + lote.length, total), total);
  }

  return acc;
}

/**
 * Apply part (or all) of a cliente credit (saldo a favor from an anulación).
 * Optional contractId+fianzaId marks that fianza's facturas pagada in the same
 * transaction (the afianzadora already holds the money), which unlocks its
 * comisión. The agent's adeudo from the original anulación is never touched.
 */
export async function registrarCreditoUso({ clienteId, creditoId, monto, nota = null, contractId = null, fianzaId = null }) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/credito-uso`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ clienteId, creditoId, monto, nota, contractId, fianzaId }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    throw err;
  }
  return data;
}

/**
 * Revert one uso of a cliente credit. The uso stays in the history flagged
 * cancelado; disponible is restored, and any payment state the uso produced
 * (facturas marked pagada with this credit) reverts in the same transaction.
 * usoIndex is the fallback for usos that predate ids.
 */
export async function cancelarCreditoUso({ clienteId, creditoId, usoId = null, usoIndex = null }) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/credito-uso-cancelar`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ clienteId, creditoId, usoId, usoIndex }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    throw err;
  }
  return data;
}

// Per-fianza participación override: the split is negotiated with the other
// agent deal by deal (50/50 is common). Persists on the fianza and re-nets its
// pending movements; cobradas/adeudos never move.
/**
 * Purge the ledger movements of a fianza that was hard-deleted from its
 * contract. The backend refuses (409 FIANZA_STILL_EXISTS) while the fianza is
 * still on the contract — live fianzas correct their money through
 * anulación/cancelación events, never by deleting records.
 *
 * @returns {Promise<{status:string, deleted:number}>}
 */
export async function purgeComisionesFianza({ contractId, fianzaId }) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/fianza-eliminada`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ contractId, fianzaId }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    throw err;
  }
  return data;
}

export async function overrideComisionSplit({ contractId, fianzaId, beneficiario, fiado }) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/override-split`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ contractId, fianzaId, beneficiario, fiado }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    throw err;
  }
  return data;
}

export async function settleComision(movId, estadoCobro) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/settle`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ movId, estadoCobro }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    throw err;
  }
  return data;
}

// ── Reparto entre asociados ─────────────────────────────────────────────────
// Owner-only. Reparto never moves comisionMonto or estadoCobro; these only
// re-cut it among asociados and socios.

async function postComisiones(path, body) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/comisiones/${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(errorFor(response.status, data));
    err.code = data.code;
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Re-apply the split in force today (fianza override, else cliente) to fianzas
 * already on the books — every movement, pendientes and cobradas.
 * @param {{cambios: {contractId: string, fianzaId: string}[], motivo?: string}} payload
 * @returns {Promise<{actualizadas: number, sinMovimientos: string[], errores: Object[]}>}
 */
export function restatearReparto(payload) {
  return postComisiones('reparto-restatear', payload);
}

export const REPARTO_BATCH_SIZE = 100;

/** restatearReparto in batches of REPARTO_BATCH_SIZE, accumulating the result. */
export async function restatearRepartoEnLotes({ cambios, motivo }, onProgress) {
  const total = (cambios || []).length;
  const acc = { actualizadas: 0, sinMovimientos: [], errores: [] };
  for (let i = 0; i < total; i += REPARTO_BATCH_SIZE) {
    const lote = cambios.slice(i, i + REPARTO_BATCH_SIZE);
    try {
      const r = await restatearReparto({ cambios: lote, motivo });
      acc.actualizadas += r.actualizadas || 0;
      acc.sinMovimientos.push(...(r.sinMovimientos || []));
      acc.errores.push(...(r.errores || []));
    } catch (e) {
      e.parcial = { ...acc };
      throw e;
    }
    onProgress?.(Math.min(i + lote.length, total), total);
  }
  return acc;
}

/**
 * Set (rows) or clear (null) one fianza's own reparto; restates its movements.
 * @param {{contractId: string, fianzaId: string, reparto: {asociadoId: string, pct: number}[]|null, motivo?: string}} payload
 */
export function overrideReparto(payload) {
  return postComisiones('reparto-override', payload);
}

/**
 * Mark (or unmark) an asociado's cut for a month as paid. The backend freezes
 * the monto from the ledger at marking time.
 * @param {{asociadoId: string, periodo: string, pagado: boolean, nota?: string}} payload
 */
export function marcarRepartoPago(payload) {
  return postComisiones('reparto-pago', payload);
}

// ── Aprobación de reparto (Fase 2) ──────────────────────────────────────────

/**
 * Create the propuesta for a reparto change the socios must approve.
 * @param {{objetivo: {tipo: 'cliente', clienteId: string}|{tipo: 'fianza', contractId: string, fianzaId: string},
 *          reparto: {asociadoId: string, pct: number}[]|null,
 *          fianzasARestatear: {contractId: string, fianzaId: string}[], motivo?: string}} payload
 * @returns {Promise<{propuestaId: string}>}
 */
export function proponerReparto(payload) {
  return postComisiones('reparto-proponer', payload);
}

export function cancelarPropuestaReparto(propuestaId) {
  return postComisiones('reparto-propuesta-cancelar', { propuestaId });
}

/**
 * Mail every socio that still has to answer (functions callable). Each send
 * mints a fresh personal link; the previous one stops working.
 * @returns {Promise<{sent: string[], failed: string[], omitidos?: boolean}>}
 */
export async function enviarAprobacionReparto(propuestaId) {
  const fn = httpsCallable(getFunctions(), 'sendRepartoAprobacionEmail');
  const res = await fn({ propuestaId });
  return res.data;
}
